import { hasRole } from '@features/auth/model/types.js';
import {
  changesetsInspect,
  changesetsShowHistory,
} from '@features/changesets/model/actions.js';
import { historyHosted } from '@features/changesets/model/inspectorToast.js';
import {
  dataViewerCutHole,
  dataViewerDrawStop,
  dataViewerSetJoining,
  dataViewerSetSplitting,
} from '@features/dataViewer/model/actions.js';
import { editableParts } from '@features/dataViewer/vertexEditing.js';
import { elevationChartClose } from '@features/elevationChart/model/actions.js';
import { eventsSetPickingStart } from '@features/events/model/actions.js';
import {
  galleryCancelShowOnTheMap,
  galleryClear,
  galleryEditPicture,
  galleryList,
  galleryRequestImage,
  gallerySetItemForPositionPicking,
  galleryShowOnTheMap,
} from '@features/gallery/model/actions.js';
import { toggledFeaturesOpacity } from '@features/map/featuresOpacity.js';
import { getMapLeafletElement } from '@features/map/hooks/leafletElementHolder.js';
import {
  mapPresetToggle,
  mapRefocus,
  mapSetFeaturesOpacity,
  mapToggleLayer,
} from '@features/map/model/actions.js';
import { steppedZoom } from '@features/map/zoomStep.js';
import { mapAreaSelectCancel } from '@features/mapArea/model/actions.js';
import { mapLibraryPreviewEnd } from '@features/mapLibrary/model/actions.js';
import {
  libraryIndexSelector,
  userShortcutsSelector,
} from '@features/mapLibrary/model/selectors.js';
import { mapLayersPanelToggle } from '@features/mapSettings/model/actions.js';
import { selectionOpener } from '@features/openInExternalApp/selectionOpener.js';
import { panoramaSetPicking } from '@features/panorama/model/actions.js';
import { toposcopeSetPickingCenter } from '@features/toposcope/model/actions.js';
import { viewshedSetPickingViewpoint } from '@features/viewshed/model/actions.js';
import type { UnknownAction } from '@reduxjs/toolkit';
import { chordPrefixCodes, chordTarget } from '@shared/chordDefinitions.js';
import {
  effectiveShortcut,
  isLayerInstalled,
} from '@shared/mapLibrary/installed.js';
import { toolDefinitions } from '@shared/toolDefinitions.js';
import { trackMatomo } from '@shared/trackMatomo.js';
import type { Shortcut } from '@shared/types/common.js';
import {
  clearMapFeatures,
  closeTool,
  deleteFeature,
  openInExternalApp,
  openTool,
  selectFeature,
  setActiveModal,
  setSelectingHomeLocation,
  type Tool,
} from './store/actions.js';
import { modalOf } from './store/activeModal.js';
import { isToolOpen, showGalleryViewerSelector } from './store/selectors.js';
import type { MyStore, RootState } from './store/store.js';

let keyTimer: number | null = null;

/** The key that started a chord, while the next one is awaited. */
let initCode: string | null = null;

const isSuspendedModal = (state: RootState) =>
  state.homeLocation.selectingHomeLocation !== false ||
  Boolean(state.gallery.pickingPositionForId) ||
  state.gallery.showPosition ||
  Boolean(state.mapArea.selecting) ||
  Boolean(state.mapLibrary.preview) ||
  state.toposcope.pickingCenter ||
  state.events.pickingStart;

/** A selected vertex goes only while its line or ring keeps enough of them. */
function vertexDeletable(state: RootState): boolean {
  const { selection } = state.main;

  if (selection?.type !== 'vertex') {
    return true;
  }

  const feature = state.trackViewer.trackGeojson?.features[selection.id];

  const vertices = feature && editableParts(feature.geometry)?.[selection.part];

  return (
    vertices !== undefined &&
    vertices.length > (feature?.geometry.type === 'Polygon' ? 3 : 2)
  );
}

// Overlays that own their open-state outside main.activeModal: the gallery
// viewer (own + Wikimedia Commons photos), and the Wikipedia preview (shown
// while loading or once previewed). The picking/selecting overlays are tracked
// by `isSuspendedModal` instead.
const isShowingModal = (state: RootState) =>
  Boolean(state.main.activeModal) ||
  Boolean(state.gallery.activeImageId) ||
  Boolean(state.wiki.preview) ||
  Boolean(state.wiki.loading);

/**
 * What Escape does now: the one action, of all the controls offering a way
 * out, that it dispatches — and so the one control to show it on.
 */
export function escapeAction(state: RootState): UnknownAction | undefined {
  const suspendedModal = isSuspendedModal(state);

  const showingModal = isShowingModal(state);

  if (state.gallery.pickingPositionForId) {
    return gallerySetItemForPositionPicking(null);
  }

  if (state.events.pickingStart) {
    return eventsSetPickingStart(false);
  }

  if (showGalleryViewerSelector(state)) {
    return state.gallery.editModel ? galleryEditPicture() : galleryClear();
  }

  // A modal in the foreground closes itself on its own listener, which fires
  // only while this handler (registered first) leaves the key unclaimed. The
  // picking overlays hide theirs and leave the map live, so they go on.
  if (showingModal && !suspendedModal) {
    return undefined;
  }

  // Before the panels and the selection: the map is in a mode of its own,
  // and leaving it is what Escape is for here.
  if (state.toposcope.pickingCenter) {
    return toposcopeSetPickingCenter(false);
  }

  if (state.panorama.picking) {
    return panoramaSetPicking(null);
  }

  if (state.viewshed.pickingViewpoint) {
    return viewshedSetPickingViewpoint(false);
  }

  // Before the chart and the panels below it: a mode waiting on a click has
  // put them away, so Escape has to be about the mode itself.
  if (state.trackViewer.joinWith) {
    return dataViewerSetJoining(null);
  }

  if (state.trackViewer.splitting) {
    return dataViewerSetSplitting(false);
  }

  if (state.mapArea.selecting) {
    return mapAreaSelectCancel();
  }

  if (state.mapLibrary.preview) {
    return mapLibraryPreviewEnd({ keep: false });
  }

  if (state.homeLocation.selectingHomeLocation !== false) {
    return setSelectingHomeLocation(false);
  }

  if (state.elevationChart.target) {
    return elevationChartClose();
  }

  // Waiting for a hole to be drawn is a mode of its own — no line is being
  // drawn yet — so leaving it must not fall through to clearing the
  // selection, which would drop the polygon the hole was meant for.
  if (state.trackViewer.drawing) {
    return dataViewerDrawStop();
  }

  if (state.trackViewer.holeFor !== null) {
    return dataViewerCutHole(null);
  }

  if (state.gallery.showPosition) {
    return galleryCancelShowOnTheMap();
  }

  const { inspected } = state.changesetInspector;

  // Listed in its element's details, a history closes before they do; not one
  // they no longer show, their object reloaded out of the layer.
  if (!showingModal && !suspendedModal && !inspected && historyHosted(state)) {
    return changesetsShowHistory(null);
  }

  if (!showingModal && !suspendedModal && state.main.selection) {
    return selectFeature(null);
  }

  // Only the tool that owns map clicks, the one holding the map in a mode; the
  // toolbar-only tools close by their own button.
  if (!showingModal && !suspendedModal && state.main.mapTool) {
    return closeTool(state.main.mapTool);
  }

  // An inspection, after its selected element and the map-click tool beside it.
  if (!showingModal && !suspendedModal && inspected) {
    return changesetsInspect(null);
  }

  return undefined;
}

const toolChapters: Record<Tool, string> = {
  changesets: 'changesets',
  'draw-lines': 'drawing',
  'draw-points': 'drawing',
  'draw-polygons': 'drawing',
  'gps-recorder': 'gps-recorder',
  'import-file': 'tracks',
  'map-details': 'map-details',
  objects: 'objects',
  panorama: 'panorama',
  'route-planner': 'route-planner',
  toposcope: 'toposcope',
  tracking: 'tracking',
};

/**
 * The manual chapter F1 opens: the open modal's `ManualHeaderButton` one, else
 * a selected note's or search result's, else the map-click tool's, else the
 * newest other tool's; undefined opens the first.
 */
export function manualChapterFor(state: RootState): string | undefined {
  const modalChapter =
    state.main.activeModal &&
    document.querySelector<HTMLElement>('.modal.show [data-manual-chapter]')
      ?.dataset['manualChapter'];

  if (modalChapter) {
    return modalChapter;
  }

  if (state.gallery.activeImageId) {
    return 'photos';
  }

  // A selection's toolbar, before the tools' it may sit beside.
  if (state.osmNotes.selected) {
    return 'osm-notes';
  }

  if (state.main.selection?.type === 'search') {
    return 'search';
  }

  // The map-click tool first: a draw tool brings 'import-file' along with it.
  const tool = state.main.mapTool ?? state.main.panelTools.at(-1);

  if (tool) {
    return toolChapters[tool];
  }

  return state.elevationChart.target ? 'tracks' : undefined;
}

export function handleEvent(event: KeyboardEvent, state: RootState) {
  const withModifiers =
    event.ctrlKey || event.altKey || event.metaKey || event.isComposing;

  const suspendedModal = isSuspendedModal(state);

  const showingModal = isShowingModal(state);

  if (!withModifiers && event.code === 'Escape') {
    // An open dropdown closes itself on Escape.
    return document.querySelector('*[aria-expanded=true]') === null
      ? escapeAction(state)
      : undefined;
  }

  // Also over a modal and in a field, where it types nothing.
  if (
    !window.fmEmbedded &&
    !withModifiers &&
    !event.shiftKey &&
    event.code === 'F1'
  ) {
    // A modal paused for picking on the map would be torn down mid-pick.
    if (state.main.activeModal?.type === 'manual' || suspendedModal) {
      // Keeps the browser's own help page closed.
      event.preventDefault();

      return undefined;
    }

    const chapter = manualChapterFor(state);

    trackMatomo(
      chapter
        ? ['trackEvent', 'Manual', 'key', chapter]
        : ['trackEvent', 'Manual', 'key'],
    );

    return setActiveModal({ type: 'manual', chapter });
  }

  if (
    event.target instanceof HTMLElement &&
    ['input', 'select', 'textarea'].includes(event.target.tagName.toLowerCase())
  ) {
    return undefined;
  }

  if (!showingModal && (event.ctrlKey || event.metaKey)) {
    if (event.code === 'KeyZ') {
      history.back();

      return undefined;
    }

    if (event.code === 'KeyY') {
      history.forward();

      return undefined;
    }
  }

  if (withModifiers) {
    return undefined;
  }

  if (showGalleryViewerSelector(state) && !state.gallery.editModel) {
    if (event.code === 'KeyS') {
      return galleryShowOnTheMap();
    }

    if (event.code === 'KeyM') {
      return galleryEditPicture();
    }
  }

  if (
    showGalleryViewerSelector(state) &&
    !state.gallery.editModel &&
    state.gallery.imageIds &&
    state.gallery.imageIds.length > 1
  ) {
    if (event.code === 'ArrowLeft') {
      return galleryRequestImage('prev');
    }

    if (event.code === 'ArrowRight') {
      return galleryRequestImage('next');
    }
  }

  // Before the maps' shortcuts, which a user could otherwise bind it to.
  if (
    !keyTimer &&
    (!showingModal || suspendedModal) &&
    event.code === 'KeyD' &&
    event.shiftKey &&
    !event.ctrlKey &&
    !event.altKey &&
    !event.metaKey
  ) {
    const opacity = toggledFeaturesOpacity(state.map.featuresOpacity);

    trackMatomo(['trackEvent', 'MapSettings', 'featuresOpacity', opacity]);

    return mapSetFeaturesOpacity(opacity);
  }

  if (
    !keyTimer &&
    (!showingModal || suspendedModal) &&
    // A preview puts the earlier layers back, so it takes no map switch.
    !state.mapLibrary.preview &&
    (!window.fmEmbedded || !state.main.embedFeatures.includes('noMapSwitch'))
  ) {
    const pressed = (shortcut: Shortcut | null | undefined) =>
      shortcut &&
      shortcut.code === event.code &&
      Boolean(shortcut.shift) === event.shiftKey &&
      Boolean(shortcut.ctrl) === event.ctrlKey &&
      Boolean(shortcut.alt) === event.altKey &&
      Boolean(shortcut.meta) === event.metaKey;

    const taken = userShortcutsSelector(state);

    const layerDef = [
      ...state.map.customLayers,
      ...libraryIndexSelector(state).filter((def) =>
        isLayerInstalled(state.map.layersSettings, def.type),
      ),
    ].find((def) =>
      pressed(effectiveShortcut(state.map.layersSettings, def, taken)),
    );

    const layerType =
      layerDef &&
      (!('layerPreview' in layerDef) ||
        !layerDef.layerPreview ||
        hasRole(state.auth.user, 'layerPreview'))
        ? layerDef.type
        : undefined;

    if (layerType) {
      return mapToggleLayer({ type: layerType });
    }

    const preset = state.map.presets.find(({ id }) =>
      pressed(state.map.layersSettings[id]?.shortcut),
    );

    if (preset) {
      return mapPresetToggle({ id: preset.id });
    }
  }

  if (
    event.code === 'Delete' &&
    // An armed mode has no toolbar of its own to delete from, so the key would
    // take away the very track it is aimed at.
    state.trackViewer.joinWith === null &&
    !state.trackViewer.splitting &&
    !keyTimer &&
    !showingModal &&
    !suspendedModal &&
    vertexDeletable(state)
  ) {
    return deleteFeature();
  }

  if (showingModal || suspendedModal) {
    if (keyTimer) {
      window.clearTimeout(keyTimer);

      keyTimer = null;

      initCode = null;
    }

    return undefined;
  }

  // By the character, wherever a layout puts it.
  if (!keyTimer && event.key === '.') {
    return mapLayersPanelToggle();
  }

  if (!window.fmEmbedded && !keyTimer && event.key === '?') {
    trackMatomo(['trackEvent', 'Manual', 'key', 'shortcuts']);

    return setActiveModal({ type: 'manual', chapter: 'shortcuts' });
  }

  if (!window.fmEmbedded && !keyTimer && chordPrefixCodes.has(event.code)) {
    initCode = event.code;

    keyTimer = window.setTimeout(() => {
      keyTimer = null;

      initCode = null;
    }, 2000);

    return 'I';
  }

  if (keyTimer && initCode) {
    if (initCode === 'KeyG') {
      const toolDefinition = toolDefinitions.find(
        (td) => td.kbd === event.code,
      );

      if (toolDefinition?.kbd) {
        // The shortcut toggles, like the menu item it stands for.
        return isToolOpen(state, toolDefinition.tool)
          ? closeTool(toolDefinition.tool)
          : openTool(toolDefinition.tool);
      }
    }

    const target = chordTarget(initCode, event.code);

    if (!target) {
      return undefined;
    }

    if ('modal' in target) {
      return setActiveModal(modalOf(target.modal));
    }

    if ('external' in target) {
      const open = selectionOpener();

      // Copying the page's address is about the page, selection or not.
      if (open && target.external !== 'copy') {
        // No action comes back for the listener to claim the key with.
        event.preventDefault();

        open(target.external);

        return undefined;
      }

      return openInExternalApp({ where: target.external });
    }

    switch (target.command) {
      case 'clear-map-features':
        return clearMapFeatures();

      case 'gallery-list':
        return galleryList('-createdAt');
    }
  }

  return undefined;
}

const zoomKeyDirections: Record<string, 1 | -1> = {
  '+': 1,
  '=': 1,
  '-': -1,
  _: -1,
};

// Leaflet's own `keyboardPanDelta`, so the step is the size users of the
// focused map already know.
const PAN_PX = 80;

const panKeyOffsets: Record<string, [number, number]> = {
  ArrowLeft: [-PAN_PX, 0],
  ArrowRight: [PAN_PX, 0],
  ArrowUp: [0, -PAN_PX],
  ArrowDown: [0, PAN_PX],
};

/**
 * Moving the map with `+`/`-` and the arrow keys. Leaflet binds its own on the
 * map container, which only receives keys once the map has been clicked; going
 * through the store instead makes them work anywhere and matches what the
 * on-screen controls do. Holding shift triples an arrow step, as Leaflet does;
 * a zoom step ignores shift, because on most layouts `+` is typed as shift-`=`
 * and there is no telling that shift from a deliberate one.
 *
 * Routing a zoom through the store also means the store learns it before the
 * map does, which is what keeps GPS following alive across a zoom — whereas an
 * arrow key carries coordinates and so ends following, being a deliberate move
 * away from the located position.
 */
export function handleMapKey(event: KeyboardEvent, state: RootState) {
  const zoomDirection = zoomKeyDirections[event.key];

  const panOffset = panKeyOffsets[event.key];

  if (
    (!zoomDirection && !panOffset) ||
    event.ctrlKey ||
    event.altKey ||
    event.metaKey ||
    event.isComposing
  ) {
    return undefined;
  }

  if (
    event.target instanceof HTMLElement &&
    ['input', 'select', 'textarea'].includes(event.target.tagName.toLowerCase())
  ) {
    return undefined;
  }

  // An open dropdown moves its own selection with the arrow keys, and this
  // handler runs in the capture phase, so it would claim them before the menu
  // ever sees them. Same signal the Escape handling above uses.
  if (document.querySelector('*[aria-expanded=true]') !== null) {
    return undefined;
  }

  // The picking/selecting overlays leave the map live underneath, so moving it
  // stays available there; a real modal takes the keys — which is also what
  // leaves the gallery viewer its own arrow-key handling.
  const suspendedModal =
    state.homeLocation.selectingHomeLocation !== false ||
    state.gallery.pickingPositionForId ||
    state.gallery.showPosition ||
    state.mapArea.selecting ||
    state.mapLibrary.preview ||
    state.events.pickingStart;

  if (isShowingModal(state) && !suspendedModal) {
    return undefined;
  }

  const map = getMapLeafletElement();

  if (!map) {
    return undefined;
  }

  if (panOffset) {
    const step = event.shiftKey ? 3 : 1;

    // Panning starts from where the map actually is, not from the store, which
    // while following runs ahead of it.
    const mapZoom = map.getZoom();

    const { lat, lng } = map.unproject(
      map
        .project(map.getCenter(), mapZoom)
        .add([panOffset[0] * step, panOffset[1] * step]),
      mapZoom,
    );

    return mapRefocus({ lat, lon: lng });
  }

  const zoom = Math.min(
    map.getMaxZoom(),
    Math.max(map.getMinZoom(), steppedZoom(state.map.zoom, zoomDirection)),
  );

  return zoom === state.map.zoom ? undefined : mapRefocus({ zoom });
}

export function attachKeyboardHandler(store: MyStore): void {
  // Capture phase, so the key is claimed before it reaches the map container
  // and Leaflet's own handler moves the map a second time on top.
  document.addEventListener(
    'keydown',
    (e) => {
      const action = handleMapKey(e, store.getState());

      if (!action) {
        return;
      }

      store.dispatch(action);

      e.preventDefault();

      e.stopPropagation();

      // The document-level handler below never sees this key, so the pending
      // multi-key sequence it would have cancelled is cleared here instead.
      if (keyTimer) {
        window.clearTimeout(keyTimer);

        keyTimer = null;

        initCode = null;
      }
    },
    { capture: true },
  );

  document.addEventListener('keydown', (e) => {
    const action = handleEvent(e, store.getState());

    if (action === 'I') {
      return;
    }

    if (keyTimer) {
      window.clearTimeout(keyTimer);

      keyTimer = null;

      initCode = null;
    }

    if (action) {
      store.dispatch(action);

      e.preventDefault();
    }
  });
}
